export const PENDING_REDIRECT_KEY = 'parcel:pending-redirect';
// Onboarding takes minutes; an abandoned one must not steer a later, unrelated welcome.
export const PENDING_REDIRECT_TTL_MS = 2 * 60 * 60 * 1000;

const isSameOriginPath = (value: string) => value.startsWith('/') && !value.startsWith('//');

/** The two onboarding stops whose URLs cannot carry a redirect: workspace creation and the welcome flow. */
export function losesRedirect(destination: string): boolean {
  return destination === '/signup/workspace' || /^\/[^/]+\/welcome$/.test(destination);
}

/** Keep a same-origin path for the end of onboarding; per tab, like the OAuth intent. */
export function storePendingRedirect(
  path: string,
  storage: Storage = window.sessionStorage,
  now = Date.now(),
): void {
  if (!isSameOriginPath(path)) return;
  try {
    storage.setItem(PENDING_REDIRECT_KEY, JSON.stringify({ path, at: now }));
  } catch {
    // A full or blocked storage only loses the record link, never the way into onboarding.
  }
}

/** The path in force, without clearing it; stale, malformed or foreign values read as nothing. */
export function peekPendingRedirect(
  storage: Storage = window.sessionStorage,
  now = Date.now(),
): string | null {
  try {
    const value = JSON.parse(storage.getItem(PENDING_REDIRECT_KEY) ?? 'null') as {
      path?: unknown;
      at?: unknown;
    } | null;
    if (!value || typeof value.path !== 'string' || typeof value.at !== 'number') return null;
    if (now - value.at > PENDING_REDIRECT_TTL_MS) return null;
    return isSameOriginPath(value.path) ? value.path : null;
  } catch {
    return null;
  }
}

/** Read and clear. */
export function takePendingRedirect(
  storage: Storage = window.sessionStorage,
  now = Date.now(),
): string | null {
  const path = peekPendingRedirect(storage, now);
  try {
    storage.removeItem(PENDING_REDIRECT_KEY);
  } catch {
    // A link that cannot be cleared lapses on its own.
  }
  return path;
}
